import moment from 'moment-timezone';
import { getHotelTimezone } from './timeZone';

/**
 * Calculates the reservation price and optionally the checkout date
 * @param {Object} params
 * @param {Object} params.room - Room object containing pricing and legacy amount
 * @param {string} params.stayTypeId - ID of the selected stay type
 * @param {Array} params.stayTypesList - List of all available stay types
 * @param {string} params.checkInDate - ISO string or formatted date string
 * @param {string} params.checkOutDate - ISO string or formatted date string
 * @returns {Object} { amount: number, checkOutDate: string|null }
 */
export const calculateReservationPrice = ({ room, stayTypeId, stayTypesList, checkInDate, checkOutDate }) => {
  if (!room) return { amount: 0, checkOutDate: null };

  const hotelTZ = getHotelTimezone();
  const checkInMoment = checkInDate ? moment.tz(checkInDate, hotelTZ) : null;
  const checkOutMoment = checkOutDate ? moment.tz(checkOutDate, hotelTZ) : null;

  const selectedStayType = stayTypesList?.find((type) => String(type._id) === String(stayTypeId));

  // --- Scenario 1: Legacy / Default Nightly Logic ---
  // If no stay type is selected, or explicitly 'night', or stay type not found in list
  if (!selectedStayType || stayTypeId === 'night') {
    // Aun en el camino legacy preferimos la Matriz de Tarifas: buscamos el tipo
    // nocturno del hotel y su precio para esta habitación.
    const nightlyType = stayTypesList?.find((type) => type.isNightly);
    const nightlyEntry = nightlyType ? room.pricing?.find((p) => String(p.stayTypeId) === String(nightlyType._id)) : null;
    const roomPrice = nightlyEntry ? parseFloat(nightlyEntry.amount) || 0 : parseFloat(room.amount) || 0;

    if (checkInMoment?.isValid() && checkOutMoment?.isValid()) {
      // Calculate nights: difference in days rounded up, min 1
      const nights = Math.max(1, Math.ceil(checkOutMoment.diff(checkInMoment, 'days', true)));
      return {
        amount: nights * roomPrice,
        checkOutDate: null // We don't auto-calculate checkout date for nightly manual entry
      };
    }
    return { amount: roomPrice, checkOutDate: null };
  }

  // --- Scenario 2: Dynamic Stay Type Logic ---
  const pricingEntry = room.pricing?.find((p) => String(p.stayTypeId) === String(selectedStayType._id));
  // Precio desde la Matriz de Tarifas. Si ese tipo de estadía no tiene precio
  // configurado para esta habitación caemos al campo legacy `room.amount`:
  // cobrar el legacy es preferible a devolver 0 y no cobrar nada.
  const price = pricingEntry ? parseFloat(pricingEntry.amount) || 0 : parseFloat(room.amount) || 0;

  // Case 2A: Nightly Dynamic Stay Type
  if (selectedStayType.isNightly) {
    if (checkInMoment?.isValid() && checkOutMoment?.isValid()) {
      const nights = Math.max(1, Math.ceil(checkOutMoment.diff(checkInMoment, 'days', true)));
      return {
        amount: nights * price,
        checkOutDate: null
      };
    }
    return { amount: price, checkOutDate: null };
  }

  // Case 2B: Hourly / Fixed Duration Stay Type
  if (selectedStayType.hours) {
    let calculatedCheckOut = null;
    if (checkInMoment?.isValid()) {
      const autoCheckOut = checkInMoment.clone().add(selectedStayType.hours, 'hours');
      calculatedCheckOut = autoCheckOut.format('YYYY-MM-DDTHH:mm');
    }

    // For hourly stays, the price is usually fixed per slot (price),
    // OR we could multiply if they stayed multiple slots (not implemented here based on previous logic).
    // Assuming 1 slot = 1 price.
    return {
      amount: price,
      checkOutDate: calculatedCheckOut
    };
  }

  // Fallback default
  return { amount: price, checkOutDate: null };
};
